import React, { useMemo, useState } from 'react';
import { PageHeader, StatusChip } from '../../components/ui';
import { formatClockTime, shopWallClockDate } from '../../utils/shopTime';
import { useShopTimezones } from '../../hooks/useShopTimezones';
import { useCachedData } from '../../hooks/useCachedData';
import { historyRange } from './appointments/helpers';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Alert,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { apiErrorMessage, appointmentsService, mechanicsService, shopsService, tasksService } from '../../services/api';
import { Appointment, AppointmentStatus, Mechanic, Shop, Task, TaskStatus, CreateTaskDto, UpdateTaskDto } from '../../types';
import { format, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns';

const NO_APPOINTMENTS: Appointment[] = [];
const NO_MECHANICS: Mechanic[] = [];
const NO_SHOPS: Shop[] = [];

const MechanicsCalendarPage: React.FC = () => {
  // The calendar shows this week, so the appointments page's default window
  // covers it — and sharing its cache entry makes either screen instant after
  // the other. It used to download every appointment the shop ever took.
  const appointmentsData = useCachedData('appointments:90d', () =>
    appointmentsService.getAllAppointments(historyRange('90d')),
  );
  const mechanicsData = useCachedData('mechanics', () => mechanicsService.getAllMechanics());
  const shopsData = useCachedData('shops', () => shopsService.getAllShops());
  const appointments = appointmentsData.data ?? NO_APPOINTMENTS;
  const mechanics = mechanicsData.data ?? NO_MECHANICS;
  const allShops = shopsData.data ?? NO_SHOPS;
  const shops = useMemo(() => allShops.filter((shop) => shop.isActive), [allShops]);
  const loading = appointmentsData.loading || mechanicsData.loading || shopsData.loading;
  const loadError = appointmentsData.error || mechanicsData.error || shopsData.error;
  const [selectedShop, setSelectedShop] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  
  // Appointment selection and task management
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [appointmentTasks, setAppointmentTasks] = useState<Task[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [taskForm, setTaskForm] = useState<{ title: string; description: string; status: TaskStatus }>({
    title: '',
    description: '',
    status: TaskStatus.PENDING,
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Appointment times are shop-local wall clock: read each in its own shop's
  // zone, so the calendar is right for a viewer in any timezone.
  const tzOf = useShopTimezones(appointments.map((appointment) => appointment.shopId));
  const shopClock = (appointment: Appointment): Date =>
    shopWallClockDate(
      appointment.appointmentDateTime || appointment.appointmentDate || 0,
      tzOf(appointment.shopId),
    );

  const getFilteredMechanics = () => {
    if (!selectedShop) return mechanics;
    return mechanics.filter(mechanic => mechanic.shopId === selectedShop);
  };

  const getAppointmentsForMechanic = (mechanicId: string) => {
    const mechanicAppointments = appointments.filter(appointment => appointment.mechanicId === mechanicId);
    const groupedByDate: { [date: string]: Appointment[] } = {};
    
    mechanicAppointments.forEach(appointment => {
      let dateStr: string;
      if (appointment.appointmentDateTime || appointment.appointmentDate) {
        dateStr = format(shopClock(appointment), 'yyyy-MM-dd');
      } else {
        return; // Skip appointments without dates
      }

      if (!groupedByDate[dateStr]) {
        groupedByDate[dateStr] = [];
      }
      groupedByDate[dateStr].push(appointment);
    });
    
    return groupedByDate;
  };

  const getCurrentWeekDays = () => {
    // "This week" on the shop's calendar, not the viewer's.
    const today = shopWallClockDate(
      Math.floor(Date.now() / 1000),
      tzOf(selectedShop || appointments[0]?.shopId),
    );
    const start = startOfWeek(today, { weekStartsOn: 1 });
    const end = endOfWeek(today, { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  };

  const handleAppointmentClick = async (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    try {
      const tasks = await tasksService.getTasksByAppointmentId(appointment.id);
      setAppointmentTasks(tasks);
    } catch (error) {
      console.error('Failed to load tasks:', error);
      setAppointmentTasks([]);
    }
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedAppointment(null);
    setAppointmentTasks([]);
    setSuccessMessage(null);
  };

  const handleOpenTaskForm = (task?: Task) => {
    if (task) {
      setEditingTask(task);
      setTaskForm({
        title: task.title,
        description: task.description || '',
        status: task.status,
      });
    } else {
      setEditingTask(null);
      setTaskForm({
        title: '',
        description: '',
        status: TaskStatus.PENDING,
      });
    }
    setTaskDialogOpen(true);
  };

  const handleSaveTask = async () => {
    if (!selectedAppointment || !taskForm.title.trim()) {
      setError('Task title is required');
      return;
    }

    try {
      setError(null);
      if (editingTask) {
        const updateData: UpdateTaskDto = {
          title: taskForm.title,
          description: taskForm.description || undefined,
          status: taskForm.status,
        };
        await tasksService.updateTask(editingTask.id, updateData);
        setSuccessMessage('Task updated successfully');
      } else {
        const createData: CreateTaskDto = {
          title: taskForm.title,
          description: taskForm.description || undefined,
          status: taskForm.status,
          appointmentId: selectedAppointment.id,
        };
        await tasksService.createTask(createData);
        setSuccessMessage('Task created successfully');
      }

      // Reload tasks
      if (selectedAppointment) {
        const tasks = await tasksService.getTasksByAppointmentId(selectedAppointment.id);
        setAppointmentTasks(tasks);
      }

      setTaskDialogOpen(false);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${editingTask ? 'update' : 'create'} task`);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      setError(null);
      await tasksService.deleteTask(taskId);
      setSuccessMessage('Task deleted successfully');

      if (selectedAppointment) {
        const tasks = await tasksService.getTasksByAppointmentId(selectedAppointment.id);
        setAppointmentTasks(tasks);
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete task');
    }
  };

  const handleUpdateTaskStatus = async (taskId: string, newStatus: TaskStatus) => {
    try {
      setError(null);
      await tasksService.updateTaskStatus(taskId, newStatus);
      setSuccessMessage('Task status updated successfully');

      if (selectedAppointment) {
        const tasks = await tasksService.getTasksByAppointmentId(selectedAppointment.id);
        setAppointmentTasks(tasks);
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update task status');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Skeleton variant="text" width={300} height={40} sx={{ mb: 3 }} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Box key={i} sx={{ flexBasis: { xs: '100%', md: '45%', lg: '30%' } }}>
              <Skeleton variant="rectangular" height={400} />
            </Box>
          ))}
        </Box>
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">{apiErrorMessage(loadError, 'Failed to fetch data')}</Alert>
      </Container>
    );
  }

  const filteredMechanics = getFilteredMechanics();

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader
        title="Mechanics calendar"
        subtitle="This week's schedule for every mechanic, one card each."
      />

      <Box sx={{ mb: 4 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="shop-filter-label">Filter by Shop</InputLabel>
          <Select
            labelId="shop-filter-label"
            value={selectedShop}
            label="Filter by Shop"
            onChange={(e) => setSelectedShop(e.target.value)}
          >
            <MenuItem value="">
              <em>All Shops</em>
            </MenuItem>
            {shops.map((shop) => (
              <MenuItem key={shop.id} value={shop.id}>
                {shop.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {[
          AppointmentStatus.PENDING,
          AppointmentStatus.CONFIRMED,
          AppointmentStatus.IN_PROGRESS,
          AppointmentStatus.READY_FOR_PICKUP,
          AppointmentStatus.CLOSED_OUT,
          AppointmentStatus.CANCELLED,
        ].map((status) => (
          <StatusChip key={status} status={status} />
        ))}
      </Box>

      {filteredMechanics.length === 0 ? (
        <Alert severity="info">
          {selectedShop 
            ? 'No mechanics found for the selected shop.' 
            : 'No mechanics found.'}
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {filteredMechanics.map((mechanic) => {
            const appointmentsByDate = getAppointmentsForMechanic(mechanic.id);
            const allAppointments = Object.values(appointmentsByDate).flat();
            const weekDays = getCurrentWeekDays();

            return (
              <Box 
                key={mechanic.id} 
                sx={{ 
                  flexBasis: { xs: '100%', lg: '48%', xl: '31%' },
                  display: 'flex'
                }}
              >
                <Card sx={{ height: '100%', width: '100%' }}>
                  <CardHeader
                    title={
                      <Box>
                        <Typography variant="h6" component="div">
                          {mechanic.user?.name || 'Unknown Mechanic'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {mechanic.specialization} • {mechanic.yearsOfExperience} years exp.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Shop: {shops.find(s => s.id === mechanic.shopId)?.name || 'Unknown Shop'}
                        </Typography>
                      </Box>
                    }
                    subheader={
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip size="small" label={`${allAppointments.length} total`} />
                        <Chip 
                          size="small" 
                          label={`${allAppointments.filter(a => a.status === AppointmentStatus.CONFIRMED).length} confirmed`} 
                          color="success" 
                        />
                        <Chip 
                          size="small" 
                          label={`${allAppointments.filter(a => a.status === AppointmentStatus.PENDING).length} pending`} 
                          color="warning" 
                        />
                        <Chip 
                          size="small"
                          label={`${allAppointments.filter(a => a.status === AppointmentStatus.IN_PROGRESS).length} in progress`}
                          color="warning"
                        />
                        <Chip
                          size="small"
                          label={`${allAppointments.filter(a => a.status === AppointmentStatus.READY_FOR_PICKUP).length} ready for pickup`}
                          color="info"
                        />
                        <Chip
                          size="small"
                          label={`${allAppointments.filter(a => a.status === AppointmentStatus.CLOSED_OUT).length} closed out`}
                          color="success"
                        />
                      </Box>
                    }
                  />
                  <CardContent>
                    <Box sx={{ height: 400, overflow: 'auto' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        This Week's Schedule
                      </Typography>
                      {weekDays.map((day) => {
                        const dateKey = format(day, 'yyyy-MM-dd');
                        const dayAppointments = appointmentsByDate[dateKey] || [];
                        
                        return (
                          <Box key={dateKey} sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                              {format(day, 'EEEE, MMM d')}
                            </Typography>
                            {dayAppointments.length === 0 ? (
                              <Typography variant="body2" color="text.secondary" sx={{ ml: 2, fontStyle: 'italic' }}>
                                No appointments
                              </Typography>
                            ) : (
                              <List dense>
                                {dayAppointments
                                  .sort(
                                    (a, b) =>
                                      Number(a.appointmentDateTime || a.appointmentDate || 0) -
                                      Number(b.appointmentDateTime || b.appointmentDate || 0),
                                  )
                                  .map((appointment) => (
                                    <ListItem 
                                      key={appointment.id} 
                                      sx={{ 
                                        py: 0.5,
                                        cursor: 'pointer',
                                        '&:hover': {
                                          backgroundColor: 'action.hover',
                                        },
                                      }}
                                      onClick={() => handleAppointmentClick(appointment)}
                                    >
                                      <ListItemText
                                        primary={
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" component="span">
                                              {appointment.appointmentDateTime
                                                ? format(shopClock(appointment), 'h:mm a')
                                                : formatClockTime(appointment.appointmentTime)
                                              }
                                            </Typography>
                                            <StatusChip status={appointment.status} />
                                          </Box>
                                        }
                                        secondary={
                                          <Box>
                                            <Typography variant="caption" display="block">
                                              {appointment.customerName} - {appointment.vehicleModel}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {appointment.serviceDescription.substring(0, 50)}
                                              {appointment.serviceDescription.length > 50 ? '...' : ''}
                                            </Typography>
                                            {appointment.comments && (
                                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                Note: {appointment.comments.substring(0, 40)}
                                                {appointment.comments.length > 40 ? '...' : ''}
                                              </Typography>
                                            )}
                                          </Box>
                                        }
                                      />
                                    </ListItem>
                                  ))}
                              </List>
                            )}
                            <Divider sx={{ mt: 1 }} />
                          </Box>
                        );
                      })}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Appointment Details Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Appointment Details
          {selectedAppointment && (
            <StatusChip status={selectedAppointment.status} sx={{ ml: 2 }} />
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedAppointment && (
            <Stack spacing={3}>
              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              {successMessage && (
                <Alert severity="success" onClose={() => setSuccessMessage(null)}>
                  {successMessage}
                </Alert>
              )}

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Customer
                </Typography>
                <Typography variant="body1">
                  {selectedAppointment.customerName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedAppointment.customerEmail} • {selectedAppointment.customerPhone}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Appointment Date & Time
                </Typography>
                <Typography variant="body1">
                  {selectedAppointment.appointmentDateTime || selectedAppointment.appointmentDate
                    ? format(shopClock(selectedAppointment), "EEEE, MMMM d, yyyy 'at' h:mm a")
                    : 'Date not available'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Vehicle
                </Typography>
                <Typography variant="body1">
                  {selectedAppointment.vehicleModel} {selectedAppointment.vehicleYear && `(${selectedAppointment.vehicleYear})`}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Service Description
                </Typography>
                <Typography variant="body1">
                  {selectedAppointment.serviceDescription}
                </Typography>
              </Box>

              {selectedAppointment.comments && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Comments
                  </Typography>
                  <Typography variant="body1">
                    {selectedAppointment.comments}
                  </Typography>
                </Box>
              )}

              {/* Tasks Section */}
              <Box sx={{ mt: 2, pt: 2, borderTop: '2px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Service Tasks</Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleOpenTaskForm()}
                    startIcon={<Add />}
                  >
                    Add Task
                  </Button>
                </Box>
                {appointmentTasks.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No tasks found for this appointment.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {appointmentTasks.map((task) => (
                      <Box
                        key={task.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1.5,
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {task.title}
                          </Typography>
                          {task.description && (
                            <Typography variant="body2" color="text.secondary">
                              {task.description}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StatusChip
                            status={task.status}
                            onClick={() => {
                              const statuses = [TaskStatus.PENDING, TaskStatus.IN_PROGRESS, TaskStatus.COMPLETED];
                              const currentIndex = statuses.indexOf(task.status);
                              const nextStatus = statuses[(currentIndex + 1) % statuses.length];
                              handleUpdateTaskStatus(task.id, nextStatus);
                            }}
                            sx={{ cursor: 'pointer' }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleOpenTaskForm(task)}
                            color="primary"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteTask(task.id)}
                            color="error"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Task Form Dialog */}
      <Dialog open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTask ? 'Edit Task' : 'Create New Task'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Task Title"
              value={taskForm.title}
              onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description (Optional)"
              value={taskForm.description}
              onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={taskForm.status}
                label="Status"
                onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value as TaskStatus })}
              >
                <MenuItem value={TaskStatus.PENDING}>Pending</MenuItem>
                <MenuItem value={TaskStatus.IN_PROGRESS}>In Progress</MenuItem>
                <MenuItem value={TaskStatus.COMPLETED}>Completed</MenuItem>
                <MenuItem value={TaskStatus.CANCELLED}>Canceled</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveTask} variant="contained" disabled={!taskForm.title.trim()}>
            {editingTask ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MechanicsCalendarPage; 